import React, { useState } from "react";
import { ArrowLeft, ArrowRight, UsersRound, ShieldCheck } from "lucide-react";
import useCtx from "../hooks/useCts";
import "firebase/auth";
import { signIn, signUp, resetPassword } from "../firebase";
import { updateClient } from "../api/clients";
import Skyline from "../assets/london-skyline.jpg";
import logo from "../assets/logo.png";
import TeacherLogin from "../components/TeacherLogin";
import { addAdminRegister } from "../api/historyAdminRegisters";
import { blurActiveElement } from "../utils/helpers";

const LoginScreen: React.FC = () => {
  const ctx = useCtx();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isRegistering, setIsRegistering] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [selectedLoginType, setSelectedLoginType] = useState<
    "admin" | "teacher" | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleErrorCode = (e: string) => {
    if (e === "auth/weak-password") {
      setError("Za słabe hasło. Spróbuj z: Duża litera, cyfra, min. 8 znaków");
    }
    if (e === "auth/email-already-in-use") {
      setError("Email istnieje w bazie. Zaloguj się.");
    }
    if (e === "auth/invalid-credential") {
      setError("Niepoprawne hasło.");
    }
    if (e === "auth/too-many-requests") {
      setError("Za dużo prób, spróbuj później");
    }
  };

  const handleAuthAction = () => {
    blurActiveElement();

    if (isRegistering) {
      if (!isAccepted) {
        setError("Musisz zaakceptować regulamin i politykę prywatności.");
        setIsLoading(false);

        return;
      }

      setError("");
      const client = ctx.clients.find((c: any) => c.adminEmail === email);
      console.log("client: ", email, ctx.clients);

      if (client) {
        signUp(email, password)
          .then(async (res) => {
            await updateClient({ id: client.id, authId: res.uid });
            handleErrorCode(res);
            await addAdminRegister({
              adminId: client.id ?? "nieznany",
              date: Date.now(),
              action: "login", // <-- kluczowe
              path: "/login",
            });
          })
          .catch((e) => setError(e))
          .finally(() => setIsLoading(false));
      } else {
        alert("Nie masz uprawnień admina, skontaktuj się z nami");
        //resetFields();
        //setIsLoading(false);
      }
    } else {
      signIn(email, password)
        .then(async (res) => {
          handleErrorCode(res);
          // znajdź klienta (admina) po emailu
          const client = ctx.clients.find((c: any) => c.adminEmail === email);

          if (client) {
            await addAdminRegister({
              adminId: client.id ? client.id : "nieznany",
              date: Date.now(),
              path: "/login",
              action: "login",
            });
          }
        })
        .catch((e) => {
          console.error("Error during login:", e);
        })
        .finally(() => setIsLoading(false));
    }
  };

  const handleForgotPassword = () => {
    blurActiveElement();

    if (email) {
      resetPassword(email)
        .then(() => {
          alert("Link do zresetowania hasła został wysłany na podany email.");
        })
        .catch((e) => {
          console.error("Error during password reset:", e);
          setError("Wystąpił błąd podczas resetowania hasła.");
        })
        .finally(() => {
          setError("");
        });
    } else {
      setError("Podaj email, aby zresetować hasło.");
    }
    resetFields();
  };

  const resetFields = () => {
    blurActiveElement();
    setEmail("");
    setPassword("");
    setIsRegistering(false);
    setIsAccepted(false);
  };

  const handleSelectLoginType = (type: "admin" | "teacher") => {
    resetFields();
    setError("");
    setIsLoading(false);
    setSelectedLoginType(type);
  };

  const handleBackToChooser = () => {
    resetFields();
    setError("");
    setIsLoading(false);
    setSelectedLoginType(null);
  };

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-[#f7f2ea]">
      <div
        aria-hidden="true"
        className="fixed inset-0 opacity-40"
        style={{
          backgroundImage: `url(${Skyline})`,
          backgroundAttachment: "fixed",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <div
        aria-hidden="true"
        className="fixed inset-0 bg-[linear-gradient(180deg,rgba(255,250,245,0.82),rgba(255,255,255,0.66)_24%,rgba(248,250,252,0.5))]"
      />
      <div
        aria-hidden="true"
        className="fixed inset-0 bg-[radial-gradient(circle_at_top_left,rgba(246,133,31,0.18),transparent_50%),radial-gradient(circle_at_88%_18%,rgba(44,150,233,0.24),transparent_54%),linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.24))]"
      />

      <div className="relative z-10 flex flex-1 items-center justify-center px-4 py-8 sm:px-6 lg:px-8">
        <div className="w-full max-w-xl sm:px-6 py-3 lg:max-w-lg">
          <div className="min-h-[400px] rounded-[28px] border border-white/50 bg-white/10 p-8 shadow-[0_20px_60px_rgba(148,163,184,0.18)] backdrop-blur-sm sm:p-10 md:min-h-[500px]">
            <div className="mb-0 flex justify-center">
              <img src={logo} alt="Logo" className="h-16 w-auto" />
            </div>

            {selectedLoginType === null ? (
              <div className="mt-8 flex min-h-[320px] flex-col justify-center">
                <div className="text-center">
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">
                    Panel logowania
                  </p>
                  <h2 className="mt-3 bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] bg-clip-text text-xl font-semibold uppercase text-transparent sm:text-2xl">
                    Wybierz typ konta
                  </h2>
                </div>

                <div className="mx-auto mt-10 w-full max-w-lg space-y-2">
                  <button
                    type="button"
                    onClick={() => handleSelectLoginType("admin")}
                    className="group flex w-full items-center justify-between border-b border-slate-300/70 py-5 text-left transition duration-300 hover:translate-x-1"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[22px] border border-white/90 bg-white/80 shadow-[0_14px_30px_rgba(148,163,184,0.16)]">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-black/5 bg-orange-50 text-[#f97315] shadow-sm">
                          <ShieldCheck
                            className="h-5.5 w-5.5"
                            strokeWidth={2.2}
                            aria-hidden="true"
                          />
                        </div>
                      </div>
                      <div className="min-w-0">
                        <p className="bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] bg-clip-text text-lg font-bold uppercase tracking-[0.05em] text-transparent">
                          Admin szkoły
                        </p>
                        <span className="mt-3 block h-[2px] w-16 rounded-full bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] transition-all duration-300 group-hover:w-24" />
                      </div>
                    </div>
                    <ArrowRight
                      className="hidden h-5 w-5 shrink-0 text-[#f97316] transition duration-300 group-hover:translate-x-1 sm:block"
                      strokeWidth={2.2}
                      aria-hidden="true"
                    />
                  </button>

                  <button
                    type="button"
                    onClick={() => handleSelectLoginType("teacher")}
                    className="group flex w-full items-center justify-between  py-5 text-left transition duration-300 hover:translate-x-1"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[22px] border border-white/90 bg-white/80 shadow-[0_14px_30px_rgba(148,163,184,0.16)]">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-black/5 bg-sky-50 text-sky-500 shadow-sm">
                          <UsersRound
                            className="h-5.5 w-5.5"
                            strokeWidth={2.2}
                            aria-hidden="true"
                          />
                        </div>
                      </div>
                      <div className="min-w-0">
                        <p className="bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] bg-clip-text text-lg font-bold uppercase tracking-[0.05em] text-transparent">
                          Lektor
                        </p>
                        <span className="mt-3 block h-[2px] w-16 rounded-full bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] transition-all duration-300 group-hover:w-24" />
                      </div>
                    </div>
                    <ArrowRight
                      className="hidden h-5 w-5 shrink-0 text-sky-600 transition duration-300 group-hover:translate-x-1 sm:block"
                      strokeWidth={2.2}
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </div>
            ) : selectedLoginType === "admin" ? (
              <>
                <button
                  type="button"
                  onClick={handleBackToChooser}
                  className="my-6 inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition hover:text-slate-900"
                >
                  <ArrowLeft size={16} strokeWidth={2.2} aria-hidden="true" />
                  Wróć do wyboru konta
                </button>

                <div className="mb-6 text-center">
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">
                    Panel logowania
                  </p>
                  <h2 className="mt-3 bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] bg-clip-text text-xl font-semibold tracking-[-0.03em] text-transparent sm:text-2xl">
                    {isRegistering
                      ? "Rejestracja jako admin szkoły"
                      : "Logowanie jako admin szkoły"}
                  </h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="email" className="sr-only">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="username"
                      autoCapitalize="none"
                      autoCorrect="off"
                      inputMode="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-base text-slate-800 shadow-[0_10px_24px_rgba(148,163,184,0.12)] outline-none transition placeholder:text-slate-400 focus:border-orange-200 focus:ring-4 focus:ring-orange-200/50 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="sr-only">
                      Hasło
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete={
                        isRegistering ? "new-password" : "current-password"
                      }
                      placeholder="Hasło"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-base text-slate-800 shadow-[0_10px_24px_rgba(148,163,184,0.12)] outline-none transition placeholder:text-slate-400 focus:border-orange-200 focus:ring-4 focus:ring-orange-200/50 sm:text-sm"
                    />
                  </div>

                  {error && (
                    <div className="rounded-2xl border border-rose-200/90 bg-rose-50/90 px-4 py-3 text-sm text-rose-700 shadow-[0_14px_30px_rgba(244,63,94,0.12)]">
                      {error}
                    </div>
                  )}

                  {isRegistering && (
                    <>
                      <div className="rounded-2xl border border-white/75 bg-white/68 px-4 py-4 text-sm text-slate-700 shadow-[0_10px_24px_rgba(148,163,184,0.1)]">
                        <label className="flex flex-wrap items-start">
                          <input
                            type="checkbox"
                            checked={isAccepted}
                            onChange={() => setIsAccepted(!isAccepted)}
                            className="mr-3 mt-1 flex-shrink-0"
                          />
                          <span className="flex-1 flex flex-wrap items-center">
                            Akceptuję
                            <a
                              href="/regulamin"
                              className="mx-1 whitespace-nowrap text-sky-700 underline"
                            >
                              regulamin
                            </a>
                            oraz
                            <a
                              href="/polityka-prywatnosci"
                              className="mx-1 whitespace-nowrap text-sky-700 underline"
                            >
                              politykę prywatności.
                            </a>
                          </span>
                        </label>
                        {!isAccepted && (
                          <p className="mt-2 text-xs text-rose-500">
                            * Aby się zarejestrować, zaznacz zgodę.
                          </p>
                        )}
                      </div>

                      <p className="text-xs leading-6 text-slate-600">
                        Administratorem danych jest{" "}
                        <strong>Directpress sp. z o.o.</strong>. Dane będą
                        przetwarzane zgodnie z{" "}
                        <a
                          href="/polityka-prywatnosci"
                          className="text-sky-700 underline"
                        >
                          polityką prywatności
                        </a>
                        .
                      </p>
                    </>
                  )}

                  <div className="space-y-3 pt-2">
                    {isRegistering ? (
                      <>
                        <button
                          onClick={handleAuthAction}
                          disabled={isLoading || !isAccepted}
                          className={`w-full rounded-full px-6 py-3 text-sm font-semibold text-white transition ${
                            isLoading
                              ? "cursor-not-allowed bg-slate-400"
                              : isAccepted
                                ? "bg-[linear-gradient(135deg,#f59e0b,#f97316)] shadow-[0_16px_36px_rgba(249,115,22,0.28)] hover:brightness-105"
                                : "cursor-not-allowed bg-orange-300"
                          }`}
                        >
                          {isLoading
                            ? "Przetwarzanie..."
                            : "Zarejestruj się jako admin"}
                        </button>

                        <p className="text-center text-sm text-slate-600">
                          {isRegistering
                            ? "Masz już konto?"
                            : "Nie masz jeszcze konta?"}{" "}
                          <button
                            onClick={() => setIsRegistering(!isRegistering)}
                            className="text-sky-700 underline focus:outline-none"
                          >
                            {isRegistering ? "Zaloguj się" : "Zarejestruj się"}
                          </button>
                        </p>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={handleAuthAction}
                          disabled={isLoading}
                          className={`w-full rounded-full px-6 py-3 text-sm font-semibold text-white transition ${
                            isLoading
                              ? "cursor-not-allowed bg-slate-400"
                              : "bg-[linear-gradient(135deg,#f59e0b,#f97316)] shadow-[0_16px_36px_rgba(249,115,22,0.28)] hover:brightness-105"
                          }`}
                        >
                          {isLoading
                            ? "Logowanie..."
                            : "Zaloguj się jako admin"}
                        </button>
                        <p className="text-center text-sm text-slate-600">
                          {isRegistering
                            ? "Masz już konto?"
                            : "Nie masz jeszcze konta?"}{" "}
                          <button
                            onClick={() => setIsRegistering(!isRegistering)}
                            className="text-sky-700 underline focus:outline-none"
                          >
                            {isRegistering ? "Zaloguj się" : "Zarejestruj się"}
                          </button>
                        </p>

                        <p
                          onClick={handleForgotPassword}
                          className="text-center text-sm text-sky-700 underline cursor-pointer"
                        >
                          Zapomniałeś hasła?
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <TeacherLogin onBack={handleBackToChooser} />
            )}
          </div>
        </div>
      </div>

      <div className="relative z-10 w-full border-t border-white/60 bg-white/60 px-4 py-8 backdrop-blur-md sm:px-6 lg:px-44">
        {/* Left: contact info */}
        <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
          <div className="flex flex-wrap justify-center gap-4 md:justify-start md:gap-6">
            <a
              href="mailto:kontakt@directpress.pl"
              className="text-xs text-slate-500 hover:underline sm:text-sm"
            >
              kontakt@directpress.pl
            </a>
            <a
              href="tel:+48666657366"
              className="text-xs text-slate-500 hover:underline sm:text-sm"
            >
              +48 666 657 366
            </a>
            <span className="text-xs text-slate-500 sm:text-sm">
              ul. Hoża 86/410, 00-682 Warszawa
            </span>
          </div>

          <div>
            <p className="text-center text-xs text-slate-500 sm:text-sm md:text-right">
              © {new Date().getFullYear()} DIRECTPRESS Sp. z o.o.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
